import { useMemo, useRef } from 'react';
import { StationeryTemplateCategoryEnum, useCardDashboardStationeryTemplatesContainerQuery } from '@graphql/generated';

import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer';
import type { StationeryTemplate } from './DesignsGallery.types';
import { processInitialQueryData } from './utils';
import { injectProducts } from './injectProducts';
import type { FacetOptionLocal } from './components/Filters/Filter.types';
import { isDigitalProduct } from '@apps/card/components/ProductUIConfig/isDigitalProduct';
import { useProducts } from './useProducts';
import { useEventDesignThemeId } from './useEventDesignThemeId';

type UseDesignTemplatesArgs = {
  eventId?: string;
  eventHandle?: string;
  categories?: StationeryTemplateCategoryEnum[];
  themeIds?: string[];
  skipDigitalDesigns?: boolean;
};

export type StationeryTemplateWithThemeJson = StationeryTemplate & { themeJSON: ThemeJson };

const themeIdWithOrientation = (template: StationeryTemplate) => {
  if (template.widthInches > template.heightInches) {
    return `wide ${template.themeId}`;
  }
  if (template.heightInches > template.widthInches) {
    return `tall ${template.themeId}`;
  }
  return template.themeId;
};

/*
 * This method allows setting order of top N items in the gallery based on array of themeIds coming from Amplitude Feature flag
 */
const setGalleryOrder = (templates: StationeryTemplate[], themeOrder: string[]): StationeryTemplate[] => {
  if (templates?.length < 1 || themeOrder?.length < 1) return templates;
  try {
    const themeLookup = new Map(themeOrder.map((themeId, index) => [themeId, index]));
    return [...templates].sort((a, b) => {
      const aIndex = themeLookup.get(themeIdWithOrientation(a)) ?? themeLookup.get(a?.themeId) ?? Number.POSITIVE_INFINITY;
      const bIndex = themeLookup.get(themeIdWithOrientation(b)) ?? themeLookup.get(b?.themeId) ?? Number.POSITIVE_INFINITY;
      return aIndex - bIndex;
    });
  } catch {
    console.error('Error sorting templates by printGalleryThemeDisplayOrder.  Using default sort order');
    return templates;
  }
};

const useDesignTemplatesCore = (currentCategory: StationeryTemplateCategoryEnum, args: UseDesignTemplatesArgs) => {
  const { eventId, categories, themeIds, skipDigitalDesigns } = args;
  const shouldSkipFetchingDigitalDesignData = !(currentCategory === StationeryTemplateCategoryEnum.invitation || currentCategory === StationeryTemplateCategoryEnum.saveTheDate);

  const { data: templateData, error } = useCardDashboardStationeryTemplatesContainerQuery({
    variables: {
      filter: {
        categories: categories ?? [currentCategory],
        side: 'front',
        themeIds,
      },
      // Requesting a number larger than what we have in the DB while we wait on
      // designs for pagination
      first: 20000,
      skipDigitalDesigns: skipDigitalDesigns || shouldSkipFetchingDigitalDesignData,
      includeOptions: {
        includeFavoritesByEventIds: eventId ? [eventId] : undefined,
        includeProducts: true,
      },
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: false,
  });

  if (error) throw error;

  const templatesWithProductIds = useMemo(() => {
    return processInitialQueryData(templateData, currentCategory);
  }, [templateData, currentCategory]);

  return templatesWithProductIds;
};

export const useDesignTemplates = (args: UseDesignTemplatesArgs) => {
  const { eventHandle } = args;
  const { currentCategory } = useCardCategoryContext();
  const templatesWithProductIds = useDesignTemplatesCore(currentCategory, args);
  const products = useProducts();

  const { value, payload } = useFeatureValue('printGalleryThemeDisplayOrder');
  const hasDisplayOrder = value === 'on';

  const eventDesignThemeId = useEventDesignThemeId(eventHandle);
  const [themeQueryParam] = useManagedSearchParam('theme');
  const themeQueryParamRef = useRef(themeQueryParam);
  const priorityThemeId = themeQueryParamRef.current || eventDesignThemeId;

  const templates = useMemo(() => {
    const templatesWithProducts = injectProducts(templatesWithProductIds, products);

    // Conceptually part of `processedInitialPrintableTemplates`. However this part can only be done after
    // injectProducts. TODO: Consider all `mappedTags`logic to post-injectProducts.
    for (const template of templatesWithProducts) {
      const templateFormats = new Set<FacetOptionLocal>(template.products.map(p => (isDigitalProduct(p) ? 'format:digital' : 'format:paper')));
      template.mappedTags.push(...Array.from(templateFormats));
    }

    let sortedTemplates: StationeryTemplate[] = [...templatesWithProducts];

    if (hasDisplayOrder) {
      if (Array.isArray(payload)) {
        sortedTemplates = setGalleryOrder(templatesWithProducts, payload);
      } else if (payload && typeof payload === 'object') {
        // Expected format:
        // {
        //   "saveTheDate": [ ... ],
        //   "invitation": [ ... ],
        //   "enclosure": [
        //     "wide modern_monogram_navy",
        //     "tall upload_your_design",
        //     "weekend_events_blush",
        //     "tall modern_monogram_navy"
        //   ],
        //   "thankYou": [ ... ],
        //   "holiday": [ ... ]
        // }

        const themeIdListByCategory = payload as Record<string, string[]>;
        const themeIdList = themeIdListByCategory[currentCategory];
        if (Array.isArray(themeIdList)) {
          sortedTemplates = setGalleryOrder(templatesWithProducts, themeIdList);
        }
      }
    }

    // Promoting certain templates to first place
    if (priorityThemeId) {
      const priorityIndex = sortedTemplates.findIndex(template => template.themeId === priorityThemeId);
      if (priorityIndex > 0) {
        const [priorityTemplate] = sortedTemplates.splice(priorityIndex, 1);
        sortedTemplates.unshift(priorityTemplate);
      }
    }

    return sortedTemplates;
  }, [templatesWithProductIds, currentCategory, products, hasDisplayOrder, priorityThemeId, payload]);

  return templates;
};
